declare var $: any;

import { Component, ViewChild, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {Title} from "@angular/platform-browser";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Stripe } from '@stripe/stripe-js';

import { LogService } from 'src/app/services/common/log.service';
import { AlertService } from 'src/app/services/alert.service';
import { FacilityService } from 'src/app/services/facility.service';
import { MemberService } from 'src/app/services/member.service';
import { StripeService } from 'src/app/services/stripe.service';

import { MemberModel } from 'src/app/models/member';

import { AuthBaseComponent } from 'src/app/components/auth-base/auth-base.component';
import { Card, PaymentMethod } from 'src/app/models/common-models';
import { AddCardComponent } from 'src/app/components/stripe/add-card/add-card.component';
import { EnvironmentService } from 'src/app/services/environment.service';
import { AddPaymentMethodComponent } from 'src/app/components/stripe/add-payment-method/add-payment-method.component';
import { VerifyBankAccountComponent } from 'src/app/components/stripe/verify-bank-account/verify-bank-account.component';

@Component({
  selector: 'df-payment-methods',
  templateUrl: './payment-methods.component.html',
  styleUrls: ['./payment-methods.component.scss']
})
export class PaymentMethodsComponent extends AuthBaseComponent implements OnInit {
  @ViewChild('addCard') addCardPopup!: AddCardComponent;

  private stripePromise: Promise<Stripe | null>;

  submitted: boolean = false;
  memberInfo!: MemberModel;
  cards!: Card[];
  paymentMethods: PaymentMethod[] = [];
  selectedPaymentMethodId: string = "";

  isLoading = false;

  context: any = undefined;

  constructor(private router: Router
    , private logService: LogService
    , private alertService: AlertService
    , private facilityService: FacilityService
    , private _memberService: MemberService
    , private _stripeService: StripeService
    , private modalService: NgbModal
    , private titleService: Title
    , private _environmentService: EnvironmentService
  ) {
    super(router, logService, alertService, facilityService);
  }

  override async ngOnInit() {
    await super.ngOnInit();
    //title
    this.titleService.setTitle(this._environmentService.getFacilityConfig().name + " Account -> Payment Methods");

    this.isLoading = true;
    this.paymentMethods = [];
    this.memberInfo = await this._memberService.getMember();
    this.paymentMethods = await this._stripeService.getCustomerPaymentMethods(this.memberInfo.Id);
    this.isLoading = false;
  }

  openAddPaymentMethodPopup() {
      setTimeout(() => {
        //open class map popup
        const modalRef = this.modalService.open(AddPaymentMethodComponent, {
          ariaLabelledBy: 'modal-basic-title',
          size: 'lg',
          centered: true,
          fullscreen: window.innerWidth <= 768,
        });
  
        //Passing itemIds to popup
        modalRef.componentInstance.memberId = this.memberInfo.Id;
    
        //Retrieving boolean from popup
        modalRef.result.then(async (result: boolean) => {
          this.logService.debug("card added?:", result);
          if(result)
          {
            this.paymentMethods = await this._stripeService.getCustomerPaymentMethods(this.memberInfo.Id);
            this.computeDefaultSelected();
          }
        });
      }, 500);
  }

  private isTruthyFlag(value: any): boolean {
    if (value === true || value === false) { return value; }
    if (value === 1 || value === 0) { return !!value; }
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      return v === 'true' || v === '1' || v === 'yes';
    }
    return false;
  }

  private computeDefaultSelected(): void {
    // 1) Try top-level default flags
    let defaultPm = (this.paymentMethods as any[]).find(pm => this.isTruthyFlag(pm?.is_default) || this.isTruthyFlag(pm?.default));

    // 2) Prefer nested default flags if top-level not present
    if (!defaultPm) {
      defaultPm = (this.paymentMethods as any[]).find(pm =>
        ((pm?.type === 'card' || (!!pm.card && !(pm.type||'').toString())) && this.isTruthyFlag(pm?.card?.is_default)) ||
        ((pm?.type === 'us_bank_account' || pm?.type === 'bank_account' || (!!pm.us_bank_account && !(pm.type||'').toString())) && this.isTruthyFlag(pm?.us_bank_account?.is_default))
      );
    }
    if (defaultPm) {
      this.selectedPaymentMethodId = this.getPaymentMethodId(defaultPm);
      return;
    }

    // Fallback to first payment method
    if (!this.selectedPaymentMethodId && this.paymentMethods.length > 0) {
      this.selectedPaymentMethodId = this.getPaymentMethodId((this.paymentMethods as any[])[0]);
    }
  }

  cardImage(brand: string) : string {
    var imageName: string = brand.toLowerCase().replace(/ /g, '_');
    var imagePath: string = `assets/images/svg/cards/${imageName}.svg`;
    const isAvailable = this.isImageAvailable(imageName);
    if(!isAvailable)
      imagePath = `assets/images/svg/cards/card.svg`
    return imagePath;
  }

  isImageAvailable(imageName: string): boolean {
    const availableImages = ['visa', 'american_express', 'discover', 'mastercard', 'us_bank_account'];
    return availableImages.includes(imageName);
  }

  getPaymentMethodId(pm: any): string {
    if (!pm) { return ''; }
    const type = (pm.type || '').toString();
    if (type === 'card' || (!!pm.card && !type)) { return pm.card?.id || pm.id || ''; }
    if (type === 'us_bank_account' || type === 'bank_account' || (!!pm.us_bank_account && !type)) { return pm.us_bank_account?.id || pm.id || ''; }
    // Fallback to top-level id if present
    return pm.id || '';
  }

  isPendingUsBankAccount(pm: any): boolean {
    const type = (pm?.type || '').toString();
    if (!(type === 'us_bank_account' || type === 'bank_account' || (!!pm?.us_bank_account && !type))) return false;

    const status = (pm?.us_bank_account?.status || '').toString().toLowerCase();
    // Stripe: 'verified' means active; everything else treat as pending/inactive
    return status !== 'verified';
  }

  async openVerifyBankAccount(pm: PaymentMethod): Promise<void> {
    const modalRef = this.modalService.open(VerifyBankAccountComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      fullscreen: window.innerWidth <= 768,
    });

    modalRef.componentInstance.paymentMethod = pm;

    try {
      const result = await modalRef.result;
      if (result) {
        this.isLoading = true;
        this.paymentMethods = await this._stripeService.getCustomerPaymentMethods(this.memberInfo.Id);
        this.isLoading = false;
        this.computeDefaultSelected();
      }
    } catch {
      // dismissed
    }
  }

  setPaymentMethodDefault(paymentMethodId: string): void {
    this.isLoading = true;
    this._stripeService.setPaymentMethodDefault(this.memberInfo.Id, paymentMethodId).subscribe((_status: boolean) => {
      window.location.reload();
    });
  }

  removeCard(cardId: string): void {
    this.isLoading = true;
    this._stripeService.removeCard(this.memberInfo.Id, cardId).subscribe((status: boolean) => {
      window.location.reload();
    });
  }
  
  toggleMenu(event: Event) {
    const menu = $(event.target).siblings('.dropdown-menu');
    $('.dropdown-menu').not(menu).hide();
    menu.toggle();
  }

  openAddCardPopup(delay: number = 0) {
    setTimeout(() => {
      //open class map popup
      const modalRef = this.modalService.open(AddCardComponent, {
        ariaLabelledBy: 'modal-basic-title',
        // size: 'lg',
        centered: true,
        fullscreen: window.innerWidth <= 768,
        // windowClass: 'custom-class'
      });

      //Passing data to popup
      modalRef.componentInstance.memberId = this.memberInfo.Id;
  
      //Retrieving info from popup
      modalRef.result.then((result: boolean) => {
        if (result) {
          this.logService.debug("Card added successfully. ", result);
          //location.reload();
        }
      });
    }, delay);
  }
}
